import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  CheckCircle2, XCircle, HelpCircle, Eye, IndianRupee,
  Building2, Calendar, AlertTriangle, Clock, FileText, Ban, Package
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const getPriorityBadge = (priority) => {
  const styles = {
    normal: { className: 'bg-gray-100 text-gray-700', label: 'Normal' },
    urgent: { className: 'bg-amber-100 text-amber-700', label: 'Urgent' },
    critical: { className: 'bg-red-100 text-red-700 animate-pulse', label: 'Critical' },
  };
  const style = styles[priority] || styles.normal;
  return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
};

const getStatusBadge = (status) => {
  const styles = {
    submitted: { className: 'border-blue-500 text-blue-600', label: 'New' },
    resubmitted: { className: 'border-purple-500 text-purple-600', label: 'Resubmitted' },
    modification_requested: { className: 'border-amber-500 text-amber-600', label: 'Mod. Request' },
  };
  const style = styles[status] || { className: '', label: status };
  return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
};

export default function PaymentApprovalsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Approval dialog
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    decision: '',
    approved_amount: '',
    approved_payment_date: '',
    modification_reason: '',
    admin_remarks: '',
    priority_override: '',
    clarification_question: ''
  });
  const [processing, setProcessing] = useState(false);
  
  // Modification approval dialog
  const [modDialogOpen, setModDialogOpen] = useState(false);
  const [modForm, setModForm] = useState({
    decision: '',
    approved_amount: '',
    approved_date: '',
    admin_remarks: ''
  });

  const fetchPendingRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/payment-requests/admin/pending`, {
        headers: getAuthHeader()
      });
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load pending requests');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  const openApprovalDialog = async (request) => {
    // Fetch full details with ledger
    try {
      const response = await axios.get(
        `${API_URL}/api/payment-requests/${request.id}`,
        { headers: getAuthHeader() }
      );
      setSelectedRequest(response.data);
      setApprovalForm({
        decision: '',
        approved_amount: response.data.amount_requested,
        approved_payment_date: response.data.payment_due_date,
        modification_reason: '',
        admin_remarks: '',
        priority_override: '',
        clarification_question: ''
      });
      setApprovalDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  const openModificationDialog = async (request) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/payment-requests/${request.id}`,
        { headers: getAuthHeader() }
      );
      setSelectedRequest(response.data);
      
      // Get latest modification request
      const latestMod = response.data.modifications?.[response.data.modifications.length - 1];
      
      setModForm({
        decision: '',
        approved_amount: latestMod?.new_amount || response.data.approved_amount,
        approved_date: latestMod?.new_date || response.data.approved_payment_date,
        admin_remarks: ''
      });
      setModDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  const handleApproval = async () => {
    if (!approvalForm.decision) {
      toast.error('Please select a decision');
      return;
    }
    
    if (approvalForm.decision === 'clarification_requested' && !approvalForm.clarification_question) {
      toast.error('Please enter your question');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.patch(
        `${API_URL}/api/payment-requests/${selectedRequest.id}/approve`,
        {
          decision: approvalForm.decision,
          approved_amount: parseFloat(approvalForm.approved_amount) || null,
          approved_payment_date: approvalForm.approved_payment_date || null,
          modification_reason: approvalForm.modification_reason || null,
          admin_remarks: approvalForm.admin_remarks || null,
          priority_override: approvalForm.priority_override || null,
          clarification_question: approvalForm.clarification_question || null
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Request ${approvalForm.decision === 'approve' ? 'approved' : approvalForm.decision === 'reject' ? 'rejected' : 'sent for clarification'}`);
      setApprovalDialogOpen(false);
      fetchPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const handleModificationApproval = async () => {
    if (!modForm.decision) {
      toast.error('Please select a decision');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.patch(
        `${API_URL}/api/payment-requests/${selectedRequest.id}/approve-modification`,
        {
          decision: modForm.decision,
          approved_amount: parseFloat(modForm.approved_amount) || null,
          approved_date: modForm.approved_date || null,
          admin_remarks: modForm.admin_remarks || null
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Modification ${modForm.decision === 'approve' ? 'approved' : 'rejected'}`);
      setModDialogOpen(false);
      fetchPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process modification');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async (request) => {
    if (!window.confirm(`Cancel payment request ${request.request_number}?`)) return;
    
    try {
      await axios.patch(
        `${API_URL}/api/payment-requests/${request.id}/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Request cancelled');
      fetchPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  // Separate new requests from modification requests
  const newRequests = requests.filter(r => ['submitted', 'resubmitted'].includes(r.status));
  const modificationRequests = requests.filter(r => r.status === 'modification_requested');

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="payment-approvals-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Payment Approvals</h1>
          <p className="text-muted-foreground">Review and approve payment requests</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="text-muted-foreground">New Requests</span>
              </div>
              <p className="text-3xl font-bold mt-2">{newRequests.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <span className="text-muted-foreground">Modification Requests</span>
              </div>
              <p className="text-3xl font-bold mt-2">{modificationRequests.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-green-500" />
                <span className="text-muted-foreground">Total Pending Amount</span>
              </div>
              <p className="text-3xl font-bold mt-2">
                {formatCurrency(requests.reduce((sum, r) => sum + (r.amount_requested || 0), 0))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* New/Resubmitted Requests */}
        <Card>
          <CardHeader>
            <CardTitle>Pending Approval</CardTitle>
            <CardDescription>New and resubmitted payment requests</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : newRequests.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                No pending requests
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newRequests.map(req => (
                    <TableRow key={req.id} className={req.priority === 'critical' ? 'bg-red-50' : req.priority === 'urgent' ? 'bg-amber-50' : ''}>
                      <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {req.supplier_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {req.payment_type?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(req.amount_requested)}
                      </TableCell>
                      <TableCell>{formatDate(req.payment_due_date)}</TableCell>
                      <TableCell>{getPriorityBadge(req.priority)}</TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{req.created_by_name}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="default" onClick={() => openApprovalDialog(req)}>
                            Review
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleCancel(req)}>
                            <Ban className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Modification Requests */}
        {modificationRequests.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-amber-600">Modification Requests</CardTitle>
              <CardDescription>Requests from accounts to modify approved payments</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Original Amount</TableHead>
                    <TableHead className="text-right">New Amount</TableHead>
                    <TableHead>Original Date</TableHead>
                    <TableHead>New Date</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modificationRequests.map(req => {
                    const latestMod = req.modifications?.[req.modifications.length - 1];
                    return (
                      <TableRow key={req.id} className="bg-amber-50">
                        <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                        <TableCell>{req.supplier_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(req.approved_amount)}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {latestMod?.new_amount ? formatCurrency(latestMod.new_amount) : '-'}
                        </TableCell>
                        <TableCell>{formatDate(req.approved_payment_date)}</TableCell>
                        <TableCell>{latestMod?.new_date ? formatDate(latestMod.new_date) : '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm">{latestMod?.reason}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="default" onClick={() => openModificationDialog(req)}>
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Payment Request</DialogTitle>
              <DialogDescription>
                {selectedRequest?.request_number} - {selectedRequest?.supplier_name}
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 py-4">
                {/* Request Details */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="text-muted-foreground">Payment Type</Label>
                    <p className="font-medium capitalize">{selectedRequest.payment_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Priority</Label>
                    <p>{getPriorityBadge(selectedRequest.priority)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Requested Amount</Label>
                    <p className="font-medium text-lg">{formatCurrency(selectedRequest.amount_requested)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Requested Due Date</Label>
                    <p className="font-medium">{formatDate(selectedRequest.payment_due_date)}</p>
                  </div>
                </div>

                {/* Purpose */}
                <div className="p-4 border rounded-lg">
                  <Label className="text-muted-foreground">Purpose / Reason</Label>
                  <p className="mt-1">{selectedRequest.purpose}</p>
                </div>

                {/* Clarification Response (if resubmitted) */}
                {selectedRequest.clarification_response && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Label className="text-blue-700">Clarification Response</Label>
                    <p className="mt-1">{selectedRequest.clarification_response}</p>
                  </div>
                )}

                {/* Supplier Outstanding */}
                {selectedRequest.current_supplier_outstanding && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Supplier Outstanding
                    </Label>
                    <div className="grid grid-cols-3 gap-4 mt-2">
                      <div>
                        <span className="text-sm text-muted-foreground">Total Invoices:</span>
                        <p className="font-semibold">{formatCurrency(selectedRequest.current_supplier_outstanding.total_invoices)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Total Paid:</span>
                        <p className="font-semibold">{formatCurrency(selectedRequest.current_supplier_outstanding.total_payments)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Outstanding:</span>
                        <p className="font-semibold text-red-600">{formatCurrency(selectedRequest.current_supplier_outstanding.closing_balance)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* GRN Summary Section */}
                {selectedRequest.grn_details && selectedRequest.grn_details.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-4 py-3 flex items-center gap-2">
                      <Package className="w-5 h-5 text-slate-600" />
                      <span className="font-semibold text-slate-700">Linked GRN Details</span>
                      <Badge variant="outline" className="ml-auto">{selectedRequest.grn_details.length} GRN(s)</Badge>
                    </div>
                    
                    {selectedRequest.grn_details.map((grn, grnIdx) => (
                      <div key={grn.id || grnIdx} className={grnIdx > 0 ? 'border-t' : ''}>
                        {/* GRN Header */}
                        <div className="grid grid-cols-4 gap-3 p-3 bg-muted/30">
                          <div>
                            <span className="text-xs text-muted-foreground">GRN No</span>
                            <p className="font-mono text-sm font-medium">{grn.store_receipt_no || grn.grn_number || '-'}</p>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">Invoice No</span>
                            <p className="font-mono text-sm">{grn.invoice_no || grn.invoice_dc_no || '-'}</p>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">PO Number</span>
                            <p className="font-mono text-sm">{grn.po_number || '-'}</p>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">GRN Date</span>
                            <p className="text-sm">{formatDate(grn.created_at)}</p>
                          </div>
                        </div>
                        
                        {/* Line Items Table */}
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/20">
                              <TableHead className="text-xs">Item</TableHead>
                              <TableHead className="text-xs text-right">Qty</TableHead>
                              <TableHead className="text-xs text-right">Rate</TableHead>
                              <TableHead className="text-xs text-right">Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(grn.items || []).map((item, idx) => (
                              <TableRow key={idx} className="text-sm">
                                <TableCell className="py-2">{item.item_name}</TableCell>
                                <TableCell className="py-2 text-right">
                                  {item.supplier_qty || item.system_qty || item.quantity_received || 0} {item.supplier_unit || item.unit || ''}
                                </TableCell>
                                <TableCell className="py-2 text-right">
                                  {formatCurrency(item.supplier_rate || item.system_rate || 0)}
                                </TableCell>
                                <TableCell className="py-2 text-right font-medium">
                                  {formatCurrency(item.supplier_amount || item.system_amount || 0)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        
                        {/* GRN Totals */}
                        <div className="flex justify-end p-3 bg-muted/20">
                          <div className="w-64 space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Subtotal</span>
                              <span>{formatCurrency(grn.total_supplier_amount || grn.total_system_amount || 0)}</span>
                            </div>
                            {(grn.other_charges_taxable || 0) > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Other Charges (Taxable)</span>
                                <span>{formatCurrency(grn.other_charges_taxable)}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">GST ({grn.gst_rate || 0}%)</span>
                              <span>{formatCurrency(grn.gst_amount || 0)}</span>
                            </div>
                            {(grn.other_charges_non_taxable || 0) > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Other Charges (Non-Taxable)</span>
                                <span>{formatCurrency(grn.other_charges_non_taxable)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold border-t pt-1">
                              <span>Grand Total</span>
                              <span>{formatCurrency(grn.grand_total || 0)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <hr />

                {/* Decision */}
                <div className="space-y-2">
                  <Label>Your Decision *</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={approvalForm.decision === 'approve' ? 'default' : 'outline'}
                      className={approvalForm.decision === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                      onClick={() => setApprovalForm(prev => ({ ...prev, decision: 'approve' }))}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant={approvalForm.decision === 'reject' ? 'default' : 'outline'}
                      className={approvalForm.decision === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}
                      onClick={() => setApprovalForm(prev => ({ ...prev, decision: 'reject' }))}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      variant={approvalForm.decision === 'clarification_requested' ? 'default' : 'outline'}
                      className={approvalForm.decision === 'clarification_requested' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      onClick={() => setApprovalForm(prev => ({ ...prev, decision: 'clarification_requested' }))}
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Need Clarification
                    </Button>
                  </div>
                </div>

                {/* Approval Fields */}
                {approvalForm.decision === 'approve' && (
                  <div className="space-y-4 p-4 border border-green-200 rounded-lg bg-green-50">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Approved Amount (₹)</Label>
                        <Input
                          type="number"
                          value={approvalForm.approved_amount}
                          onChange={(e) => setApprovalForm(prev => ({ ...prev, approved_amount: e.target.value }))}
                        />
                        {parseFloat(approvalForm.approved_amount) !== selectedRequest.amount_requested && (
                          <p className="text-xs text-amber-600">Amount differs from requested</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Approved Payment Date</Label>
                        <Input
                          type="date"
                          value={approvalForm.approved_payment_date}
                          onChange={(e) => setApprovalForm(prev => ({ ...prev, approved_payment_date: e.target.value }))}
                        />
                      </div>
                    </div>
                    
                    {(parseFloat(approvalForm.approved_amount) !== selectedRequest.amount_requested || 
                      approvalForm.approved_payment_date !== selectedRequest.payment_due_date) && (
                      <div className="space-y-2">
                        <Label>Reason for Modification</Label>
                        <Textarea
                          placeholder="Explain why you changed the amount/date..."
                          value={approvalForm.modification_reason}
                          onChange={(e) => setApprovalForm(prev => ({ ...prev, modification_reason: e.target.value }))}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Priority Override (optional)</Label>
                      <Select 
                        value={approvalForm.priority_override} 
                        onValueChange={(v) => setApprovalForm(prev => ({ ...prev, priority_override: v === 'keep_original' ? '' : v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Keep original priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="keep_original">Keep original</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Rejection Reason */}
                {approvalForm.decision === 'reject' && (
                  <div className="space-y-2 p-4 border border-red-200 rounded-lg bg-red-50">
                    <Label>Rejection Reason</Label>
                    <Textarea
                      placeholder="Explain why this request is rejected..."
                      value={approvalForm.admin_remarks}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, admin_remarks: e.target.value }))}
                    />
                  </div>
                )}

                {/* Clarification Question */}
                {approvalForm.decision === 'clarification_requested' && (
                  <div className="space-y-2 p-4 border border-amber-200 rounded-lg bg-amber-50">
                    <Label>Your Question *</Label>
                    <Textarea
                      placeholder="What do you need clarification on?"
                      value={approvalForm.clarification_question}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, clarification_question: e.target.value }))}
                    />
                  </div>
                )}

                {/* General Remarks */}
                {approvalForm.decision === 'approve' && (
                  <div className="space-y-2">
                    <Label>Admin Remarks (optional)</Label>
                    <Textarea
                      placeholder="Any notes for records..."
                      value={approvalForm.admin_remarks}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, admin_remarks: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleApproval} 
                disabled={processing || !approvalForm.decision}
                className={
                  approvalForm.decision === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                  approvalForm.decision === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                  ''
                }
              >
                {processing ? 'Processing...' : 'Submit Decision'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modification Approval Dialog */}
        <Dialog open={modDialogOpen} onOpenChange={setModDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Review Modification Request</DialogTitle>
              <DialogDescription>
                {selectedRequest?.request_number} - Modification from Accounts
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 py-4">
                {/* Original vs New */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <Label className="text-muted-foreground">Original</Label>
                    <p className="font-semibold">{formatCurrency(selectedRequest.approved_amount)}</p>
                    <p className="text-sm">{formatDate(selectedRequest.approved_payment_date)}</p>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-amber-700">Requested</Label>
                    <p className="font-semibold">{formatCurrency(modForm.approved_amount)}</p>
                    <p className="text-sm">{formatDate(modForm.approved_date)}</p>
                  </div>
                </div>

                {/* Reason */}
                <div className="p-3 border rounded-lg">
                  <Label className="text-muted-foreground">Reason for Modification</Label>
                  <p className="mt-1">{selectedRequest.modifications?.[selectedRequest.modifications.length - 1]?.reason}</p>
                </div>

                {/* Decision */}
                <div className="space-y-2">
                  <Label>Your Decision *</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={modForm.decision === 'approve' ? 'default' : 'outline'}
                      className={modForm.decision === 'approve' ? 'bg-green-600' : ''}
                      onClick={() => setModForm(prev => ({ ...prev, decision: 'approve' }))}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant={modForm.decision === 'reject' ? 'default' : 'outline'}
                      className={modForm.decision === 'reject' ? 'bg-red-600' : ''}
                      onClick={() => setModForm(prev => ({ ...prev, decision: 'reject' }))}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>

                {modForm.decision === 'approve' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Final Amount (₹)</Label>
                      <Input
                        type="number"
                        value={modForm.approved_amount}
                        onChange={(e) => setModForm(prev => ({ ...prev, approved_amount: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Final Date</Label>
                      <Input
                        type="date"
                        value={modForm.approved_date}
                        onChange={(e) => setModForm(prev => ({ ...prev, approved_date: e.target.value }))}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea
                    placeholder="Any notes..."
                    value={modForm.admin_remarks}
                    onChange={(e) => setModForm(prev => ({ ...prev, admin_remarks: e.target.value }))}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setModDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleModificationApproval} 
                disabled={processing || !modForm.decision}
              >
                {processing ? 'Processing...' : 'Submit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
